<template>
  <div class="v2-orders-page">
    <div
      v-if="!isLoaded"
      class="v2-page-loading"
    >
      <v2-spinner
        size="lg"
        label="Loading orders..."
      />
    </div>

    <template v-else>
      <!-- Empty state (no orders and no filters applied) -->
      <div v-if="!orders.length && !isFilterApplied">
        <div class="v2-flex v2-justify-center v2-p-12">
          <v2-card class="v2-text-center v2-p-8">
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="v2-orders-page__empty-icon"
            >
              <line
                x1="22"
                y1="2"
                x2="11"
                y2="13"
              />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
            <p class="v2-text-muted v2-mt-4">
              There are no orders yet.
            </p>
          </v2-card>
        </div>
      </div>

      <div v-else>
        <!-- Header -->
        <div class="v2-flex v2-justify-between v2-items-center v2-mb-4">
          <h1 class="v2-heading-sm">
            Orders
          </h1>
          <div class="v2-orders-page__header-actions">
            <!--
              Ship All — bulk shipment creation, ported from V1 OrderList.
              Brand-only, gated by KP_2829_BULK_SHIPMENT, hidden on the
              dashboard route. Restored under KP-3220.
            -->
            <v2-button
              v-if="isBrand && isBulkShipmentEnabled && !isDashboardPage"
              variant="primary"
              title="Bulk create shipments for a collection"
              @click="showShipAllWizard = true"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <rect
                  x="1"
                  y="3"
                  width="15"
                  height="13"
                />
                <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                <circle
                  cx="5.5"
                  cy="18.5"
                  r="2.5"
                />
                <circle
                  cx="18.5"
                  cy="18.5"
                  r="2.5"
                />
              </svg>
              Ship All
            </v2-button>
            <v2-button
              variant="outline"
              @click="showExportModal = true"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line
                  x1="12"
                  y1="15"
                  x2="12"
                  y2="3"
                />
              </svg>
              Export Orders
            </v2-button>
          </div>
        </div>

        <!-- Tabs -->
        <v2-tabs
          :tabs="tabsList"
          :active-tab="currentTab"
          @tab-change="onTabChange"
        />

        <!-- Filters row -->
        <div class="v2-flex v2-flex-wrap v2-items-center v2-gap-3 v2-mb-4 v2-orders-page__filters">
          <div class="v2-orders-page__filter-item">
            <v2-dropdown
              :options="collectionNames"
              :value="queryParams.collectionName"
              placeholder="Collection"
              :searchable="true"
              @select="onSelectCollectionName"
              @search="loadCollectionNames"
            />
          </div>
          <div class="v2-orders-page__filter-item">
            <v2-dropdown
              :options="entityNames"
              :value="queryParams.entityName"
              :placeholder="isBrand ? 'Retailer' : 'Brand'"
              :searchable="true"
              @select="onSelectEntityName"
              @search="loadEntityNames"
            />
          </div>
          <div class="v2-orders-page__filter-item v2-orders-page__filter-item--sm">
            <v2-dropdown
              :options="statusOptions"
              :value="queryParams.status"
              placeholder="Status"
              label-key="label"
              value-key="value"
              @select="onSelectStatus"
            />
          </div>
          <div class="v2-orders-page__filter-item">
            <v2-date-range-picker
              :key="`order-date-${filtersClearKey}`"
              :value="filterDate"
              placeholder="Order date"
              @change="onSelectOrderDate"
            />
          </div>
          <div class="v2-orders-page__filter-item">
            <v2-date-range-picker
              :key="`expected-date-${filtersClearKey}`"
              :value="expectedDate"
              placeholder="Estimated Delivery Window"
              @change="onSelectExpectedDate"
            />
          </div>
          <div
            v-if="(isEntityAdmin || isSalesManager) && isBrand && isLoaded"
            class="v2-orders-page__filter-item"
          >
            <v2-reps-filter-dropdown
              :key="`reps-${filtersClearKey}`"
              :options="salesReps"
              :show-self-option="true"
              @on-select-options="onSelectedReps"
            />
          </div>
          <div
            v-if="isEntityAdmin && isBrand && isLoaded"
            class="v2-orders-page__filter-item"
          >
            <v2-reps-filter-dropdown
              :key="`managers-${filtersClearKey}`"
              :options="salesManagers"
              placeholder="Sales manager"
              :show-self-option="true"
              @on-select-options="onSelectedReps"
            />
          </div>
          <!-- KP-3336: Clear filters — appears only when a filter-row selection
               is active. Ghost button so it reads as a subtle inline action
               next to the filter chips. -->
          <button
            v-if="hasActiveFilters"
            type="button"
            class="v2-orders-page__clear-filters"
            @click="onClearFilters"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line
                x1="18"
                y1="6"
                x2="6"
                y2="18"
              />
              <line
                x1="6"
                y1="6"
                x2="18"
                y2="18"
              />
            </svg>
            Clear filters
          </button>
        </div>

        <!-- Table -->
        <v2-card :no-padding="true">
          <v2-table
            :columns="tableColumns"
            :items="orders"
            :clickable-rows="true"
            @row-click="openOrderDetails"
          >
            <template #empty>
              <div class="v2-orders-page__empty-filtered">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="v2-orders-page__empty-icon"
                >
                  <polyline points="23 4 23 10 17 10" />
                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                </svg>
                <p class="v2-text-muted v2-mt-2">
                  No orders for this request.
                  <br>Please, change your filter settings.
                </p>
              </div>
            </template>

            <!-- Column: Sales Rep Name -->
            <template #cell(salesRepName)="{ item }">
              <span class="v2-text-nowrap">
                {{ item.isCreatedBySelf ? profile.entityName : item.salesRepName }}
              </span>
            </template>

            <!-- Column: Order ID -->
            <!--
              KP-3336: render the order id as a real <a href> pointing at the
              PLG-wrapped order URL, so right-click / Cmd-click / middle-click
              open the order in a new tab *inside the authenticated PLG shell*
              (opening the raw B2B URL standalone has no auth and shows a generic
              page). A plain left-click is intercepted and handled as in-app SPA
              navigation so it stays within the current iframe session.
            -->
            <template #cell(orderSeqId)="{ item }">
              <a
                class="v2-orders-page__order-link"
                :href="orderDetailHref(item)"
                @click.stop="onOrderLinkClick(item, $event)"
              >
                {{ item.orderSeqId }}
              </a>
            </template>

            <!-- Column: Retailer -->
            <template #cell(retailerName)="{ item }">
              <span class="v2-text-nowrap">
                {{ item.retailerName }} {{ item.retailerBrandIdentity ? `(${item.retailerBrandIdentity})` : '' }}
              </span>
            </template>

            <!-- Column: Brand -->
            <template #cell(brandName)="{ item }">
              <span class="v2-text-nowrap">
                {{ item.brandName }}
              </span>
            </template>

            <!-- Column: Collection -->
            <template #cell(collectionName)="{ item }">
              <span class="v2-text-nowrap">
                {{ item.collectionName }}
              </span>
            </template>

            <!-- Column: Order Date -->
            <template #cell(createdAt)="{ item }">
              <span class="v2-text-nowrap">
                {{ getDateFromTimestamp(item.createdAt) }}
              </span>
            </template>

            <!-- Column: Time -->
            <template #cell(time)="{ item }">
              <span class="v2-text-nowrap">
                {{ getFormattedTimeFromDate(item.createdAt) }}
              </span>
            </template>

            <!-- Column: Status -->
            <template #cell(status)="{ item }">
              <v2-badge :variant="getStatusBadgeVariant(item.status)">
                {{ getStatusText(item.status) }}
              </v2-badge>
            </template>

            <!-- Column: Total Units -->
            <template #cell(totalQuantity)="{ item }">
              <span>{{ item.totalQuantity }}</span>
            </template>

            <!-- Column: Total Amount -->
            <template #cell(totalPrice)="{ item }">
              <span class="v2-text-nowrap">
                {{ formatCurrency(item.totalPriceAfterDiscount || item.totalPrice, currency(item)) }}
              </span>
            </template>
          </v2-table>
        </v2-card>

        <!-- Pagination -->
        <v2-pagination
          v-if="hasPagination"
          :current-page="queryParams.page"
          :total-items="totalOrders"
          :per-page="queryParams.limit"
          @page-change="onPageChange"
        />
      </div>
    </template>

    <!-- Export Modal -->
    <v2-modal
      :show="showExportModal"
      title="Export Orders"
      size="sm"
      @close="showExportModal = false"
    >
      <div class="v2-orders-page__export-options">
        <button
          v-for="option in exportOptions"
          :key="option.value"
          class="v2-orders-page__export-option"
          :disabled="isExportingOrders"
          @click="handleExportOrders(option)"
        >
          <span>{{ option.text }}</span>
          <v2-spinner
            v-if="isExportingOrders && exportingOption === option.value"
            size="sm"
          />
        </button>
      </div>
    </v2-modal>

    <!--
      Ship All Wizard — v2 reimplementation using V2Modal + V2 components
      throughout (no Bootstrap-Vue). Logic, network calls, and step flow
      mirror the V1 wizard at views/apps/order/ship-all/ShipAllWizard.vue;
      only the presentation layer changed.
      `:show` is the single source of truth for visibility (V2Modal pattern),
      and `@closed` both hides the modal and reloads the orders list.
    -->
    <v2-ship-all-wizard
      v-if="isBrand && isBulkShipmentEnabled"
      :show="showShipAllWizard"
      @closed="onShipAllWizardClosed"
    />
  </div>
</template>

<script>
import V2Spinner from '@/components/v2/V2Spinner.vue'
import V2Card from '@/components/v2/V2Card.vue'
import V2Button from '@/components/v2/V2Button.vue'
import V2Tabs from '@/components/v2/V2Tabs.vue'
import V2Table from '@/components/v2/V2Table.vue'
import V2Pagination from '@/components/v2/V2Pagination.vue'
import V2Badge from '@/components/v2/V2Badge.vue'
import V2Modal from '@/components/v2/V2Modal.vue'
import V2Dropdown from '@/components/v2/V2Dropdown.vue'
import V2DateRangePicker from '@/components/v2/V2DateRangePicker.vue'
import V2RepsFilterDropdown from '@/views/v2/connections/V2RepsFilterDropdown.vue'
import { useOrderList } from '@/composables/useOrderList'
import analytics from '@/@core/utils/analytics'
import store from '@/store'
import constants, { FLAG_CONSTANTS } from '@/constants'
import { getPlgUrl } from '@/router/routes/utils'
import V2ShipAllWizard from './ship-all/V2ShipAllWizard.vue'

export default {
  name: 'V2OrderList',
  components: {
    V2Spinner,
    V2Card,
    V2Button,
    V2Tabs,
    V2Table,
    V2Pagination,
    V2Badge,
    V2Modal,
    V2Dropdown,
    V2DateRangePicker,
    V2RepsFilterDropdown,
    V2ShipAllWizard,
  },
  data() {
    return {
      showExportModal: false,
      exportingOption: null,
      showShipAllWizard: false,
      // KP-3336: bumped on Clear filters to remount the reps dropdowns and the
      // date-range pickers — their selection is internal (flatpickr / the reps
      // component), so they don't reset from queryParams alone, and re-feeding
      // the date picker's :value re-triggers change (which loops).
      filtersClearKey: 0,
      // KP-3336: this view is kept alive (see LayoutV2). `hasActivated` lets the
      // activated() hook skip the very first activation — created() has already
      // loaded then — and only refresh on subsequent re-entries (e.g. Back from
      // an order detail), reusing the retained filters in queryParams.
      hasActivated: false,
    }
  },
  computed: {
    tabsList() {
      return [
        { key: this.TABS.ALL, label: 'All' },
        { key: this.TABS.MARKETPLACE, label: 'Marketplace' },
        { key: this.TABS.DIRECT, label: 'Direct' },
      ]
    },
    statusOptions() {
      return this.getOrderStatus.map(status => ({
        value: status,
        label: this.ORDER_STATUS_LABELS[status],
      }))
    },
    exportOptions() {
      return Object.values(this.EXPORT_ORDERS_OPTIONS)
    },
    isDashboardPage() {
      return this.$route.name === 'dashboard'
    },
    isBulkShipmentEnabled() {
      return store.getters['flags/hasFeature'](FLAG_CONSTANTS.KP_2829_BULK_SHIPMENT)
    },
  },
  created() {
    this.initOrderList()
    analytics.track(
      this.isBrand
        ? constants.TRACKS.PAGES.BRAND_OPENS_ORDERS
        : constants.TRACKS.PAGES.RETAILER_OPENS_ORDERS,
    )
  },
  // KP-3336: kept-alive view. Skip the first activation (created() already
  // loaded), then refresh on every Back/return so changes made in the order
  // detail are reflected — while queryParams (the filters) stay retained.
  activated() {
    if (!this.hasActivated) {
      this.hasActivated = true
      return
    }
    this.loadOrders()
  },
  methods: {
    openOrderDetails(item) {
      this.$router.push({
        name: 'v2-order/detail',
        params: { orderId: item._id },
      })
    },
    // KP-3336: PLG-wrapped order URL for the row link's href. Opening this in a
    // new tab loads the authenticated PLG shell, which deep-links the iframe to
    // this order. Falls back to the in-app B2B route when not embedded / no PLG
    // URL configured (standalone use).
    orderDetailHref(item) {
      const plgUrl = getPlgUrl()
      if (plgUrl) return `${plgUrl}/retailer/orders/order/${item._id}`
      return this.$router.resolve({ name: 'v2-order/detail', params: { orderId: item._id } }).href
    },
    onOrderLinkClick(item, event) {
      // Let modified / middle clicks fall through to the href so the browser
      // opens the PLG-wrapped order URL in a new tab/window. Plain left-click is
      // handled in-app so it stays within the current iframe session.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button === 1) return
      event.preventDefault()
      this.openOrderDetails(item)
    },
    onTabChange(tab) {
      this.currentTab = tab
      this.onLoadTab(tab)
    },
    // KP-3336: clear the filter-row selections and force the reps dropdowns to
    // reset their internal state.
    onClearFilters() {
      this.filtersClearKey += 1
      this.clearFilters()
    },
    onPageChange(page) {
      this.queryParams.page = page
      this.loadOrders()
    },
    // V2 wizard emits `closed` for any termination (user dismiss, post-Done,
    // or post-completion). Hide the modal locally and reload orders so any
    // newly-created shipments are reflected.
    onShipAllWizardClosed() {
      this.showShipAllWizard = false
      this.loadOrders()
    },
    getStatusBadgeVariant(status) {
      const cssClass = this.getOrderStatusCssClass(status)
      const variantMap = {
        cancelled: 'destructive',
        rejected: 'destructive',
        confirmed: 'success',
        modified: 'warning',
        'partially-confirmed': 'primary',
        pending: 'default',
      }
      return variantMap[cssClass] || 'default'
    },
    async handleExportOrders(option) {
      this.exportingOption = option.value
      const result = await this.onExportOrders(option)
      if (result?.success) {
        this.showExportModal = false
        if (!result.usedProductionFlow) {
          analytics.track(
            this.isBrand
              ? constants.TRACKS.ACTIONS.BRAND_EXPORTS_ORDERS
              : constants.TRACKS.ACTIONS.RETAILER_EXPORTS_ORDERS,
          )
        }
      }
      this.exportingOption = null
    },
  },
  setup() {
    const orderList = useOrderList()

    return {
      ...orderList,
    }
  },
}
</script>

<style lang="scss" scoped>
.v2-orders-page {
  &__header-actions {
    display: flex;
    align-items: center;
    gap: var(--v2-space-2);
  }

  &__filter-item {
    min-width: 210px;

    &--sm {
      min-width: 130px;
      flex-basis: 130px;
    }
  }

  // KP-3336: inline "Clear filters" pill that sits in the filter row next to
  // the chips. A rounded-full glass pill (distinct from the rectangular filter
  // dropdowns so it reads as an action, not another filter) that tints toward
  // the destructive colour on hover to signal "reset", with a small X spin.
  &__clear-filters {
    display: inline-flex;
    align-items: center;
    gap: var(--v2-space-1-5);
    height: 38px;
    padding: 0 var(--v2-space-3);
    background: var(--v2-glass-surface-1);
    border: 1px solid var(--v2-glass-stroke);
    border-radius: var(--v2-radius-full);
    color: var(--v2-muted-foreground);
    font-family: var(--v2-font-sans);
    font-size: 0.8125rem;
    font-weight: 500;
    line-height: 1;
    white-space: nowrap;
    cursor: pointer;
    transition: color 0.18s ease, border-color 0.18s ease,
      background 0.18s ease, box-shadow 0.18s ease;

    svg {
      flex-shrink: 0;
      opacity: 0.9;
    }

    &:hover {
      color: var(--v2-destructive);
      border-color: var(--v2-destructive);
      background: var(--v2-glass-surface-2);

      svg {
        opacity: 1;
      }
    }

    &:active {
      transform: scale(0.97);
    }

    &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 2px var(--v2-ring);
    }
  }

  &__empty-icon {
    color: var(--v2-muted-foreground);
    margin: 0 auto;
    display: block;
  }

  &__empty-filtered {
    text-align: center;
    padding: var(--v2-space-8) var(--v2-space-4);
  }

  &__order-link {
    background: none;
    border: none;
    color: inherit;
    font-weight: inherit;
    font-size: inherit;
    font-family: inherit;
    text-decoration: none;
    cursor: pointer;
    padding: 0;
    white-space: nowrap;
  }

  &__export-options {
    display: flex;
    flex-direction: column;
    gap: var(--v2-space-2);
  }

  &__export-option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: var(--v2-space-3) var(--v2-space-4);
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    color: var(--v2-foreground);
    // Glass card — these options sit inside a dropdown panel; surface-2
    // gives the lighter nested treatment matching V2Card variant="card".
    background: var(--v2-glass-surface-2);
    border: 1px solid var(--v2-glass-stroke);
    border-radius: var(--v2-radius-md);
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease;

    &:hover:not(:disabled) {
      background: var(--v2-glass-surface-1);
      border-color: var(--v2-glass-stroke-strong);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

.v2-text-nowrap {
  white-space: nowrap;
}

.v2-text-center {
  text-align: center;
}

.v2-text-muted {
  color: var(--v2-muted-foreground);
}
</style>
